import React, { useState } from 'react';
import globalResilienceData from '../../data/globalResilienceData.json';
import CountryFlag from '../common/countryFlag/country-flag';
import SelectCustom from '../common/react-select-custom/select-custom';
import HalfCircleChart from '../common/halfCircleChart/HalfCircleChart';
import ResetButton from '../common/resetButton/ResetButton';
import { formatRank } from '../../utils/utils';
import styles from './Compare.module.scss';

const Compare = ({ baseUrl = '' }) => {
  const [selectedCountries, setSelectedCountries] = useState([null, null, null, null]);

  // Prepare country options for select (exclude already selected countries)
  const getAvailableCountryOptions = () => {
    const selectedCodes = selectedCountries
      .filter(country => country !== null)
      .map(country => country.iso2);

    return globalResilienceData
      .filter(country => !selectedCodes.includes(country.iso2))
      .map(country => ({
        value: country.iso2,
        label: country.country
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Calculate rank for each country
  const calculateRank = (countryData) => {
    if (!countryData) return null;
    const sortedCountries = [...globalResilienceData].sort((a, b) =>
      (b.totalScore || 0) - (a.totalScore || 0)
    );
    return sortedCountries.findIndex(country =>
      country.iso2 === countryData.iso2
    ) + 1;
  };

  const handleCountrySelect = (option, index) => {
    const newSelectedCountries = [...selectedCountries];

    if (option) {
      const countryData = globalResilienceData.find(country =>
        country.iso2 === option.value
      );
      newSelectedCountries[index] = countryData;
    } else {
      // Clear this selection and all subsequent ones
      for (let i = index; i < 4; i++) {
        newSelectedCountries[i] = null;
      }
    }

    setSelectedCountries(newSelectedCountries);
  };

  // const resetAll = () => {
  //   setSelectedCountries([null, null, null, null]);
  // };

  const renderColumn = (country, index) => {
    const isEnabled = index === 0 || selectedCountries[index - 1] !== null;

    // Show select box if no country selected
    if (!country) {
      return (
        <div className={`${styles.selectBox} ${!isEnabled ? styles.disabled : ''}`}>
          <label className={styles.selectLabel}>Select a Country/Territory</label>
          <SelectCustom
            value={null}
            options={getAvailableCountryOptions()}
            onChange={(option) => handleCountrySelect(option, index)}
            placeholder="Type here"
            isSearchable={true}
            isClearable={false}
            isDisabled={!isEnabled}
            selectType="passport"
            darkTheme={isEnabled}
          />
        </div>
      );
    }

    // Show country card if country selected
    const rank = calculateRank(country);

    return (
       <div className={styles.countryCard}>
          <div className={styles.cardHeader}>
              <div className={styles.resetRow}>
                  <ResetButton onClick={() => handleCountrySelect(null, index)} />
              </div>
              <div className={styles.countryRow}>
                  <CountryFlag countryName={country.country} code={country.iso2} />
              </div>
          </div>
           <div className={styles.statSection}>
                  <div className={styles.statGroup}>
                      <div className={styles.statItem}>
                          <span className={styles.statLabel}>Rank</span>
                          <span className={`${styles.statValue} ${styles.statValueLg}`}>{formatRank(rank)}</span>
                      </div>

                      <div className={styles.statItem}>
                          <span className={styles.statLabel}>Total Global Investment Risk and Resilience Index Score (Out of 100)</span>
                          <span className={`${styles.statValue} ${styles.statValueLg}`}>{country.totalScore?.toFixed(2) || 0}</span>
                      </div>

                      <div className={styles.statItem}>
                          <span className={styles.statLabel}>Investment Category</span>
                          <span className={`${styles.statValue} ${styles.statValueLg}`}>{country.scoreCategory || '-'}</span>
                      </div>
                  </div>

                  <div className={styles.statGroup}>
                      <div className={styles.statItem}>
                          <span className={styles.statLabel}>Region</span>
                          <span className={styles.statValue}>{country.region || '-'}</span>
                      </div>

                      <div className={styles.statItem}>
                          <span className={styles.statLabel}>Income Group (Gross National Income)</span>
                          <span className={styles.statValue}>{country.incomeGroup || '-'}</span>
                      </div>

                      <div className={styles.statItem}>
                          <span className={styles.statLabel}>GDP Growth (5-Year Average)</span>
                          <span className={styles.statValue}>{country.gdpGrowth || '-'}</span>
                      </div>
                  </div>
              </div>

           <div className={styles.chartSection}>
              <HalfCircleChart
                riskScore={country.riskScore || 0}
                resilienceScore={country.resilienceScore || 0}
                riskCategory={country.riskCategory}
                resilienceCategory={country.resilienceCategory}
              />
            </div>

           <div className={styles.buttonSection}>
                <button
                  className={styles.viewScorecardButton}
                  onClick={() => {
                    if (baseUrl) {
                      window.location.href = `${baseUrl}?countryCode=${country.iso2}`;
                    }
                  }}
                  disabled={!baseUrl}
                >
                  View Scorecard
                </button>
              </div>
       </div>
    );
  };

  return (
      <div className={styles.compareRow}>
        {selectedCountries.map((country, index) => (
          <React.Fragment key={index}>
            {renderColumn(country, index)}
          </React.Fragment>
        ))}
      </div>
  );
};

export default Compare;